import React, { useEffect, useState } from 'react'
import { useMask } from '@react-input/mask'
import { customerData } from '../utilities/CustomerData'
import { apiURL } from '../utilities/URLs'
import { useNavigate } from 'react-router-dom'
import { Autocomplete, useLoadScript } from '@react-google-maps/api'
import { refreshAccessToken } from '../utilities/extensions'

export default function Send8822() {
  const navigate = useNavigate()
  const [autocomplete, setAutocomplete] = useState(null)

  const libraries = ['places']

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  })

  const handleLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance)
  }

  const handleRecentPlaceChanged = () => {
    const getAddressComponent = (type, useShortName = false) => {
      const component = place.address_components.find(comp => comp.types.includes(type))
      return component ? (useShortName ? component.short_name : component.long_name) : ''
    }

    const place = autocomplete.getPlace()
    if (place) {
      setFormData((prevForm) => ({
        ...prevForm,
        recentAddress: `${getAddressComponent('street_number')} ${getAddressComponent('route')}`.trim(),
        recentCity: getAddressComponent('locality'),
        recentState: getAddressComponent('administrative_area_level_1'),
        recentZip: getAddressComponent('postal_code'),
      }))
    }
  }

  const handlePlaceChanged = () => {
    const getAddressComponent = (type, useShortName = false) => {
      const component = place.address_components.find(comp => comp.types.includes(type))
      return component ? (useShortName ? component.short_name : component.long_name) : ''
    }

    const place = autocomplete.getPlace()
    if (place) {
      if(formData.liveInUS) {
        setFormData((prevForm) => ({
          ...prevForm,
          address: `${getAddressComponent('street_number')} ${getAddressComponent('route')}`.trim(),
          city: getAddressComponent('locality'),
          state: getAddressComponent('administrative_area_level_1'),
          zip: getAddressComponent('postal_code'),
          country: getAddressComponent('country', true)
        }))
      } else {
        setFormData((prevForm) => ({
          ...prevForm,
          address: `${getAddressComponent('street_number')} ${getAddressComponent('route')}`.trim(),
          foreignCountryName: getAddressComponent('country', true),
          foreignCountryProvince: getAddressComponent('administrative_area_level_1'),
          foreignCountryCode: getAddressComponent('postal_code')
        }))
      }
    }
  }

  const [formData, setFormData] = useState({
    leadId: customerData.customerLeadData.leadId,
    customerId: customerData.customerLeadData.leadContact.contactId,
    currentLeadStageId: customerData.customerLeadData.leadStage.leadStageId,
    firstName: '',
    lastName: '',
    SSN: '',
    spouseFirstName: '',
    spouseLastName: '',
    spouseSSN: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    foreignCountryName: '',
    foreignCountryProvince: '',
    foreignCountryCode: '',
    customerLink: customerData.customerDocuments.taxDocument2021UF,
    liveInUS: true,
    customerInfo: customerData.customerLeadData.leadContact,
  })

  const [download, setDownloading] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    const newValue = type === 'radio' ? (value === 'true') : value
    setFormData({ ...formData, [name]: newValue })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (customerData.customerDocuments.marriedFilingJoint && (formData.spouseFirstName.length === 0 || formData.spouseLastName.length === 0 || formData.spouseSSN.length === 0)) {
      setError('Please fill out spouse information for married filing jointly.')
      return
    }

    if (formData.liveInUS) {
    } else {
      if (formData.address.length === 0 || formData.foreignCountryName.length === 0 || formData.foreignCountryProvince.length === 0 || formData.foreignCountryCode.length === 0) {
        setError('Please fill out all foreign address fields.')
        return
      }
    }

    setDownloading(true)

    const form = new FormData()
    form.append('customerFormInfo', JSON.stringify(formData))

    const pdfDownloadResponse = await fetch(`${apiURL}/api/generate-8822/`, {
      method: 'POST',
      body: form,
      headers: {
        'Authorization': customerData.accessToken
      }
    })

    if(pdfDownloadResponse.ok) {
      const blob = await pdfDownloadResponse.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'f8822.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setDownloading(false)
      navigate('/dashboard') 
    } else {
      const pdfDownloadData = await pdfDownloadResponse.json()
      if(pdfDownloadData.response.error) {
        if(pdfDownloadData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleSubmit(e) : navigate('/', { state: { expired: true } })
        } else
          console.error(pdfDownloadData.response.error)
      }
    }
  }

  const ssnRef = useMask({
    mask: '___-__-____',
    replacement: { _: /\d/ }
  })

  const spouseSSNRef = useMask({
    mask: '___-__-____',
    replacement: { _: /\d/ }
  })

  return (
    <div className='flex items-center justify-center bg-white'>
      <form onSubmit={handleSubmit} className='lg:w-full w-11/12 max-w-2xl mt-10'>
        <h1 className='lg:text-2xl text-lg font-bold mb-2 text-gray-800 text-center'>
          Before we download, let's get some information to put on your 8822 document
        </h1>
        <p className='text-sm italic mb-6 text-gray-600 text-center'>
          NOTE: We will NOT store this information. Please make sure this information matches on your most recent tax return
        </p>
        <div>
          <h2 className='text-xl font-bold mb-4 text-gray-800'>Personal Information</h2>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='mb-4'>
              <label className='block text-gray-700 text-sm font-bold mb-2'>First Name<span className='text-red-500'>*</span>:</label>
              <input
                type='text'
                name='firstName'
                placeholder='John'
                value={formData.firstName}
                onChange={handleChange}
                required
                className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
              />
            </div>
            <div className='mb-4'>
              <label className='block text-gray-700 text-sm font-bold mb-2'>Last Name<span className='text-red-500'>*</span>:</label>
              <input
                type='text'
                name='lastName'
                placeholder='Doe'
                value={formData.lastName}
                onChange={handleChange}
                required
                className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
              />
            </div>
          </div>
        </div>
        <div className='mb-4'>
          <label className='block text-gray-700 text-sm font-bold mb-2'>SSN<span className='text-red-500'>*</span>:</label>
          <input
            ref={ssnRef}
            type='text'
            name='SSN'
            value={formData.SSN}
            onChange={handleChange}
            required
            placeholder='XXX-XX-XXXX'
            className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
          />
        </div>
        {(customerData.customerDocuments.marriedFilingJoint) && (
          <div>
            <h2 className='text-xl font-bold mt-6 mb-4 text-gray-800'>Spouse Information</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-bold mb-2'>Spouse First Name<span className='text-red-500'>*</span>:</label>
                <input
                  type='text'
                  name='spouseFirstName'
                  placeholder='John'
                  value={formData.spouseFirstName}
                  onChange={handleChange}
                  className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                />
              </div>
              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-bold mb-2'>Spouse Last Name<span className='text-red-500'>*</span>:</label>
                <input
                  type='text'
                  name='spouseLastName'
                  placeholder='Doe'
                  value={formData.spouseLastName}
                  onChange={handleChange}
                  className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                />
              </div>
            </div>
            <div className='mb-4'>
              <label className='block text-gray-700 text-sm font-bold mb-2'>Spouse SSN<span className='text-red-500'>*</span>:</label>
              <input
                ref={spouseSSNRef}
                type='text'
                name='spouseSSN'
                placeholder='XXX-XX-XXXX'
                className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                value={formData.spouseSSN}
                onChange={handleChange}
              />
            </div>
          </div>
        )}
        <div className='mb-4'>
          <label className='block text-gray-700 text-sm font-bold mb-2'>Do you live in the United States?</label>
          <div>
            <label className='inline-flex items-center'>
              <input
                type='radio'
                name='liveInUS'
                value='true'
                checked={formData.liveInUS}
                onChange={handleChange}
                className='form-radio h-4 w-4 text-blue-500'
              />
              <span className='ml-2'>Yes</span>
            </label>
            <label className='inline-flex items-center ml-6'>
              <input
                type='radio'
                name='liveInUS'
                value='false'
                checked={!formData.liveInUS}
                onChange={handleChange}
                className='form-radio h-4 w-4 text-blue-500'
              />
              <span className='ml-2'>No</span>
            </label>
          </div>
        </div>
        {formData.liveInUS ? (
          <div>
            <h2 className='text-xl font-bold mt-6 mb-4 text-gray-800'>Address Information</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {isLoaded && (
                <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
                  <div className='mb-4'>
                    <label className='block text-gray-700 text-sm font-bold mb-2'>Address:<span className='text-red-500'>*</span></label>
                    <input
                      type='text'
                      name='address'
                      placeholder='123 Park Dr'
                      value={formData.address}
                      onChange={handleChange}
                      className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                    />
                  </div>
                </Autocomplete>
              )}
              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-bold mb-2'>City<span className='text-red-500'>*</span>:</label>
                <input
                  type='text'
                  name='city'
                  placeholder='City'
                  value={formData.city}
                  onChange={handleChange}
                  className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                />
              </div>
              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-bold mb-2'>State<span className='text-red-500'>*</span>:</label>
                <input
                  type='text'
                  name='state'
                  placeholder='State'
                  value={formData.state}
                  onChange={handleChange}
                  className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                />
              </div>
              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-bold mb-2'>Zip<span className='text-red-500'>*</span>:</label>
                <input
                  type='text'
                  name='zip'
                  placeholder='12345'
                  value={formData.zip}
                  onChange={handleChange}
                  className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                />
              </div>
            </div>
          </div>
        ) : (
          <div>
            <h2 className='text-xl font-bold mt-6 mb-4 text-gray-800'>Foreign Address Information</h2>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-bold mb-2'>Address<span className='text-red-500'>*</span>:</label>
                <input
                  type='text'
                  name='address'
                  placeholder='123 Park Dr'
                  value={formData.address}
                  onChange={handleChange}
                  className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                />
              </div>
            </Autocomplete>
              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-bold mb-2'>Foreign Country Name<span className='text-red-500'>*</span>:</label>
                <input
                  type='text'
                  name='foreignCountryName'
                  placeholder='Canada'
                  value={formData.foreignCountryName}
                  onChange={handleChange}
                  className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                />
              </div>
              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-bold mb-2'>Province<span className='text-red-500'>*</span>:</label>
                <input
                  type='text'
                  name='foreignCountryProvince'
                  placeholder='Ontario'
                  value={formData.foreignCountryProvince}
                  onChange={handleChange}
                  className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                />
              </div>
              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-bold mb-2'>Code<span className='text-red-500'>*</span>:</label>
                <input
                  type='text'
                  name='foreignCountryCode'
                  value={formData.foreignCountryCode}
                  onChange={handleChange}
                  className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                />
              </div>
            </div>
          </div>
        )}
        <div className='flex items-center justify-center mt-6 flex-col mb-6'>
          <button
            disabled={download}
            type='submit'
            className={`${!download ? 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200' : 'bg-gray-400 cursor-not-allowed'} text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline`}
          >
            Download PDF
          </button>
          {error.length > 0 && (
            <p className='text-red-700'>{error}</p>
          )}
          {download && (
            <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
              <div className="loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4"></div>
              <p>{'Downloading PDF...'}</p>
            </div>
          )}
        </div>
      </form>
    </div>
  )
}
